import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, Button, Chip, CircularProgress, Divider,
} from "@mui/material";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const D = {
  bg:          "#0a0a0f",
  surface:     "#111118",
  surfaceHigh: "#16161f",
  border:      "#1e1e2e",
  borderMed:   "#2a2a3e",
  accent:      "#7c6aff",
  accentSoft:  "rgba(124,106,255,0.12)",
  accentBorder:"rgba(124,106,255,0.25)",
  text:        "#e8e8f0",
  textSec:     "#a0a0b8",
  textMuted:   "#6b6b8a",
  success:     "#22c55e",
  successSoft: "rgba(34,197,94,0.08)",
  successBorder:"rgba(34,197,94,0.2)",
  warn:        "#f59e0b",
  warnSoft:    "rgba(245,158,11,0.08)",
  danger:      "#ef4444",
  dangerSoft:  "rgba(239,68,68,0.08)",
  font:        "'Inter', sans-serif",
  fontDisplay: "'Inter', sans-serif",
  fontMono:    "'JetBrains Mono', 'Fira Code', monospace",
};

const STATUS_META = {
  queued:      { label: "Queued",      color: D.textMuted,  bg: "rgba(107,107,138,0.08)",  border: "rgba(107,107,138,0.2)"  },
  processing:  { label: "Generating",  color: D.accent,     bg: D.accentSoft,              border: D.accentBorder           },
  ready:       { label: "Ready",       color: D.warn,       bg: D.warnSoft,                border: "rgba(245,158,11,0.25)"  },
  in_progress: { label: "In Progress", color: D.accent,     bg: D.accentSoft,              border: D.accentBorder           },
  delivered:   { label: "Delivered",   color: D.success,    bg: D.successSoft,             border: D.successBorder          },
  failed:      { label: "Failed",      color: D.danger,     bg: D.dangerSoft,              border: "rgba(239,68,68,0.2)"    },
};

const FORMAT_LABELS = { reels: "🎬 Reel", ads: "📢 Ad" };

export default function MyVideos() {
  const navigate   = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  const token      = () => localStorage.getItem("token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => {
    if (!token()) { navigate("/login"); return; }
    axios.get(`${API}/api/video/my-requests`, { headers: authHeaders() })
      .then(r => { setRequests(r.data.requests || []); setLoading(false); })
      .catch(() => { setError("Failed to load requests."); setLoading(false); });
  }, []);

  const handleDownload = async (jobId, fmt, dur) => {
    try {
      const r = await axios.get(`${API}/api/video/download/${jobId}`, {
        headers: authHeaders(), responseType: "blob",
      });
      const url  = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `video_package_${fmt}_${dur}s.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch { alert("Download failed. Package may not be ready yet."); }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: D.bg, py: 5, px: 2 }}>
      <Box sx={{ maxWidth: 720, mx: "auto" }}>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "22px", color: D.text }}>🎬 My Video Requests</Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mt: 0.5 }}>Your video production packages — script, visual assets, transcripts</Typography>
          </Box>
          <Button
            onClick={() => navigate("/video")}
            variant="contained"
            sx={{ background: D.accent, textTransform: "none", fontWeight: 600, borderRadius: "10px", fontSize: "13px",
              "&:hover": { background: "#6b5ce7" } }}
          >
            + New Request
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: D.accent }} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2.5, background: D.dangerSoft, border: "1px solid rgba(239,68,68,0.25)", borderRadius: "10px", mb: 3 }}>
            <Typography sx={{ fontSize: "13px", color: D.danger }}>{error}</Typography>
          </Box>
        )}

        {!loading && !error && requests.length === 0 && (
          <Card sx={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: "16px", p: 5, textAlign: "center" }}>
            <Typography sx={{ fontSize: "32px", mb: 2 }}>🎬</Typography>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "16px", color: D.text, mb: 1 }}>No requests yet</Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mb: 3 }}>Submit your first video request to get a complete production package.</Typography>
            <Button onClick={() => navigate("/video")} variant="contained"
              sx={{ background: D.accent, textTransform: "none", fontWeight: 600, borderRadius: "10px",
                "&:hover": { background: "#6b5ce7" } }}>
              Create Video Request
            </Button>
          </Card>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {requests.map(req => {
            const meta   = STATUS_META[req.status] || STATUS_META.queued;
            const canDl  = ["ready", "in_progress", "delivered"].includes(req.status);
            const hasUrl = !!req.video_url;

            return (
              <Card key={req.job_id} sx={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px" }}>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: D.text }}>
                          {FORMAT_LABELS[req.format] || req.format} · {req.duration}s
                        </Typography>
                        <Chip label={req.style?.replace("_", " ") || ""} size="small"
                          sx={{ background: "rgba(255,255,255,0.04)", color: D.textMuted, fontSize: "10px", height: "18px" }} />
                      </Box>
                      <Typography sx={{ fontSize: "12.5px", color: D.textSec, fontStyle: "italic",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        "{req.post_content}"
                      </Typography>
                    </Box>
                    <Chip
                      label={meta.label}
                      size="small"
                      sx={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
                        fontSize: "11px", height: "22px", fontWeight: 600, flexShrink: 0 }}
                    />
                  </Box>

                  {req.admin_notes && (
                    <Box sx={{ mb: 2, p: 1.5, background: D.surfaceHigh, borderRadius: "8px", border: `1px solid ${D.border}` }}>
                      <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Note from team</Typography>
                      <Typography sx={{ fontSize: "13px", color: D.textSec }}>{req.admin_notes}</Typography>
                    </Box>
                  )}

                  <Divider sx={{ borderColor: D.border, mb: 2 }} />

                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                    {canDl && (
                      <Button
                        onClick={() => handleDownload(req.job_id, req.format, req.duration)}
                        variant="outlined" size="small"
                        sx={{ borderColor: D.accent, color: D.accent, textTransform: "none", fontSize: "12px", borderRadius: "8px",
                          "&:hover": { borderColor: D.accent, background: D.accentSoft } }}
                      >
                        ↓ Download Package
                      </Button>
                    )}
                    {hasUrl && (
                      <Button
                        onClick={() => window.open(req.video_url, "_blank")}
                        variant="contained" size="small"
                        sx={{ background: D.success, textTransform: "none", fontSize: "12px", borderRadius: "8px", color: "#fff",
                          "&:hover": { background: "#16a34a" } }}
                      >
                        ▶ Watch Video
                      </Button>
                    )}
                    <Typography sx={{ fontSize: "11px", color: D.textMuted, ml: "auto", fontFamily: D.fontMono }}>
                      {req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>

        <Button onClick={() => navigate("/home")} sx={{ mt: 4, textTransform: "none", color: D.textMuted, fontSize: "13px" }}>
          ← Back to Home
        </Button>
      </Box>
    </Box>
  );
}
